import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TDeductionResolutionStatus, DeductionBackupDocument } from "../interface/deductionIncomplete.interface";
import { DeductionDocument } from "@/core/entities/deduction";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { RemittanceRepository } from "@/infrastructure/repositories/remittance";
import { DeleteDeductionBackupDocumentUseCase, GetIncompleteDeductionResolutionStatusUseCase } from "@/core/usecases/remittance";
import { RaiseBackupRequestUseCase } from "@/core/usecases/remittance/requestBackup";
import { BackupRequestRepository } from "@/infrastructure/repositories/remittance/backupRequest/BackupRequest.repository";
import { BackupRequestDTO } from "@/core/dto/deduction/remittance/backupRequest/backupRequest.dto";
import { DeductionIncompleteContext } from "./DeductionIncompleteContext";
import { createLogger } from "@/infrastructure/services/logger";

export const DeductionIncompleteProvider = ({ children }: { children: React.ReactNode }) => {
  const logger = createLogger("DeductionIncompleteProvider");
  const { drawerInfo } = useDeductionDrawer();
  const [remittanceItemId, setRemittanceItemId] = useState<number | undefined>(drawerInfo?.remittanceItemId);
  const [purchaserKey, setPurchaserKey] = useState<string | undefined>();
  const [deductionResolutionStatus, setDeductionResolutionStatus] = useState<TDeductionResolutionStatus>("incomplete");
  const [documents, setDocuments] = useState<DeductionDocument[]>([]);
  const [isLoadingDeductionStatus, setIsLoadingDeductionStatus] = useState<boolean>(false);
  const [importedFiles, setImportedFiles] = useState<DeductionBackupDocument[]>([]);
  const [importedFilesAsDocuments, setImportedFilesAsDocuments] = useState<DeductionDocument[]>([]);

  const [openBackupRequestModal, setOpenBackupRequestModal] = useState<boolean>(false);
  const prevRemittanceItemIdRef = useRef<number | undefined>(drawerInfo?.remittanceItemId);

  const clearAllData = () => {
    setRemittanceItemId(undefined);
    setDeductionResolutionStatus("incomplete");
    setDocuments([]);
    setIsLoadingDeductionStatus(false);
    setImportedFiles([]);
    setImportedFilesAsDocuments([]);
  };

  useEffect(() => {
    const newRemittanceItemId = drawerInfo?.remittanceItemId;

    // If remittanceItemId has changed, clear all data for the new invoice
    if (prevRemittanceItemIdRef.current !== undefined && newRemittanceItemId !== prevRemittanceItemIdRef.current) {
      startTransition(() => {
        clearAllData();
        setRemittanceItemId(newRemittanceItemId);
      });
    } else {
      startTransition(() => {
        setRemittanceItemId(newRemittanceItemId);
      });
    }

    prevRemittanceItemIdRef.current = newRemittanceItemId;
  }, [drawerInfo]);

  // Get processing status for imported file
  const getProcessingStatus = useCallback((importedFile: DeductionBackupDocument) => {
    if (importedFile.response?.error) return "FAILED";
    if (importedFile.isPending) return "PENDING";
    return "SUCCESS";
  }, []);

  // Convert imported files to DeductionDocument format for display
  const getImportedFilesAsDocuments = useCallback((): DeductionDocument[] => {
    return importedFiles.map(importedFile => {
      const status = getProcessingStatus(importedFile);

      // For uploaded files, use server fileId; for local files, use a hash of fileId to create a numeric ID
      const documentId = importedFile.response?.data?.backup?.fileId
        ? Number(importedFile.response.data.backup.fileId)
        : importedFile.fileId.split("").reduce((acc, char) => acc + (char.codePointAt(0) ?? 0), 0);

      return new DeductionDocument({
        id: documentId,
        fileName: importedFile.file.name,
        fileSize: importedFile.file.size,
        createdAt: new Date().toLocaleDateString(),
        updatedAt: new Date().toLocaleDateString(),
        documentType: "Backup",
        documentTypeKey: "BACKUP",
        fileMimeType: importedFile.file.type,
        fileDisplayName: importedFile.response?.data?.backup?.displayName ?? importedFile.file.name,
        filePath: importedFile.filePath,
        signedUrl: "",
        deletable: true,
        isProcessed: status,
      });
    });
  }, [importedFiles, getProcessingStatus]);

  const getAllFilesAsDocuments = useCallback((): DeductionDocument[] => {
    if (importedFiles.length > 0) {
      return [...getImportedFilesAsDocuments(), ...documents];
    }
    return documents;
  }, [importedFiles, documents, getImportedFilesAsDocuments]);

  useEffect(() => {
    startTransition(() => {
      setImportedFilesAsDocuments(getAllFilesAsDocuments());
    });
  }, [importedFiles, documents, getAllFilesAsDocuments]);

  const getDeductionStatus = useCallback(
    async (refetch: boolean = true) => {
      setIsLoadingDeductionStatus(true);
      const client = GraphQlAdapter.getInstance().getClient();
      const remittanceRepository = new RemittanceRepository(client);
      const deductionStatusUseCase = new GetIncompleteDeductionResolutionStatusUseCase(remittanceRepository);
      const deductionStatus = await deductionStatusUseCase.execute({ remittance_item_id: drawerInfo?.remittanceItemId ?? 0 }, refetch);

      // Convert plain objects to DeductionDocument instances
      const documents =
        deductionStatus?.deductionResolutionStatus?.documents?.map(
          (doc: any) =>
            new DeductionDocument({
              id: doc.id,
              fileName: doc.fileName,
              fileSize: doc.fileSize,
              createdAt: doc.createdAt,
              updatedAt: doc.updatedAt,
              documentType: doc.documentType,
              documentTypeKey: doc.documentTypeKey,
              fileMimeType: doc.fileMimeType,
              fileDisplayName: doc.fileDisplayName,
              filePath: doc.filePath,
              signedUrl: "", // API doesn't return signedUrl, so we set it to empty string
              deletable: doc.deletable,
              isProcessed: doc.isProcessed,
            })
        ) ?? [];
      setDocuments(documents);
      setPurchaserKey(deductionStatus?.deductionResolutionStatus?.purchaserKey);
      // Determine new status
      let newStatus: "incomplete" | "processing" | "failed";
      if (documents.some(document => document.isProcessed === "FAILED")) {
        newStatus = "failed";
      } else if (documents.length > 0) {
        newStatus = "processing";
      } else if (importedFiles.length > 0) {
        // Keep processing status if there are imported files
        newStatus = "processing";
      } else {
        newStatus = "incomplete";
      }

      setDeductionResolutionStatus(newStatus);
      setIsLoadingDeductionStatus(false);
    },
    [drawerInfo, importedFiles.length]
  );

  // Get only pending files (files that haven't been uploaded yet and don't have errors)
  const getPendingFiles = useCallback((): DeductionBackupDocument[] => {
    return importedFiles.filter(
      file =>
        // Include files that haven't been uploaded yet (no response or no fileId)
        !file.response?.data?.backup?.fileId &&
        // Exclude files that have errors
        !file.response?.error
    );
  }, [importedFiles]);

  // Helper function to get numeric ID from fileId (for local files)
  const getNumericIdFromFileId = useCallback((fileId: string): number => {
    return fileId.split("").reduce((acc, char) => acc + (char.codePointAt(0) ?? 0), 0);
  }, []);

  // Helper function to handle local file deletion by string IDs
  const handleLocalFileDeletion = useCallback((localFileIds: Set<string | number>) => {
    if (localFileIds.size === 0) return;
    setImportedFiles(prevFiles => prevFiles.filter((file: DeductionBackupDocument) => !localFileIds.has(file.fileId)));
  }, []);

  // Helper function to find files to remove from state and collect server file IDs
  const findFilesToRemove = useCallback(
    (numericIds: Set<number | string>, files: DeductionBackupDocument[]) => {
      const filesToRemove: DeductionBackupDocument[] = [];
      const serverFileIds: number[] = [];

      for (const file of files) {
        const localNumericId = getNumericIdFromFileId(file.fileId);
        const serverFileId = file.response?.data?.backup?.fileId;

        if (numericIds.has(localNumericId)) {
          filesToRemove.push(file);
        }

        if (serverFileId && numericIds.has(Number(serverFileId))) {
          filesToRemove.push(file);
          serverFileIds.push(Number(serverFileId));
        }

        if (numericIds.has(file.processId)) {
          filesToRemove.push(file);
          serverFileIds.push(file.processId);
        }
      }

      return { filesToRemove, serverFileIds };
    },
    [getNumericIdFromFileId]
  );

  // Helper function to find server file IDs from documents array
  const findServerFileIdsFromDocuments = useCallback((numericIds: Set<number | string>, docs: DeductionDocument[]) => {
    const serverFileIds: number[] = [];
    for (const doc of docs) {
      if (numericIds.has(doc.id)) {
        serverFileIds.push(doc.id);
      }
    }
    return serverFileIds;
  }, []);

  // Helper function to remove files from importedFiles state
  const removeFilesFromState = useCallback((filesToRemove: DeductionBackupDocument[]) => {
    if (filesToRemove.length === 0) return;
    const fileIdsToRemove = new Set(filesToRemove.map(f => f.fileId));
    setImportedFiles(prevFiles => prevFiles.filter(file => !fileIdsToRemove.has(file.fileId)));
  }, []);

  // Helper function to execute server deletion and cleanup
  const executeServerDeletion = useCallback(
    async (serverFileIds: number[]) => {
      if (serverFileIds.length === 0) return;

      const client = GraphQlAdapter.getInstance().getClient();
      const remittanceRepository = new RemittanceRepository(client);
      const deleteDeductionBackupDocumentUseCase = new DeleteDeductionBackupDocumentUseCase(remittanceRepository);
      const deleteDeductionBackupDocument = await deleteDeductionBackupDocumentUseCase.execute({
        document_ids: serverFileIds.map(Number),
        remittance_item_id: remittanceItemId ?? 0,
      });

      if (!deleteDeductionBackupDocument) return;

      const deletedDocumentIds = new Set(deleteDeductionBackupDocument.deleteDeductionBackupDocument.documentIds);

      setDocuments(prevDocuments => prevDocuments.filter((doc: DeductionDocument) => !deletedDocumentIds.has(doc.id)));

      setImportedFiles(prevFiles =>
        prevFiles.filter((file: DeductionBackupDocument) => {
          const fileId = file.response?.data?.backup?.fileId;
          return !fileId || !deletedDocumentIds.has(fileId);
        })
      );
    },
    [remittanceItemId]
  );

  const deleteDeductionBackupDocument = useCallback(
    async (documentIds: (number | string)[]) => {
      if (documentIds.length === 0) return;

      const localFileIds = new Set(documentIds.filter(id => typeof id === "string"));
      const numericIds = new Set(documentIds.filter(id => typeof id === "number"));

      handleLocalFileDeletion(localFileIds);

      if (numericIds.size === 0) return;

      const { filesToRemove, serverFileIds: serverFileIdsFromFiles } = findFilesToRemove(numericIds, importedFiles);
      const serverFileIdsFromDocs = findServerFileIdsFromDocuments(numericIds, documents);
      const allServerFileIds = [...serverFileIdsFromFiles, ...serverFileIdsFromDocs];

      removeFilesFromState(filesToRemove);

      try {
        await executeServerDeletion(allServerFileIds);
      } catch (error) {
        logger.error("Server deletion failed:", error);
        throw error;
      }
    },
    [handleLocalFileDeletion, findFilesToRemove, importedFiles, findServerFileIdsFromDocuments, documents, removeFilesFromState, executeServerDeletion]
  );

  const raiseBackupRequest = useCallback(async (params: BackupRequestDTO) => {
    try {
      const client = GraphQlAdapter.getInstance().getClient();
      const backupRequestRepository = new BackupRequestRepository(client);
      const raiseBackupRequestUseCase = new RaiseBackupRequestUseCase(backupRequestRepository);
      const response = await raiseBackupRequestUseCase.execute(params);
      return response;
    } catch (error) {
      logger.error("Raise backup request failed:", error);
      throw error;
    }
  },
    []
  );

  const contextValue = useMemo(
    () => ({
      remittanceItemId,
      setRemittanceItemId,
      purchaserKey,
      setPurchaserKey,
      deductionResolutionStatus,
      setDeductionResolutionStatus,
      documents,
      setDocuments,
      isLoadingDeductionStatus,
      setIsLoadingDeductionStatus,
      clearAllData,
      importedFiles,
      setImportedFiles,
      deleteDeductionBackupDocument,
      importedFilesAsDocuments,
      setImportedFilesAsDocuments,
      getAllFilesAsDocuments,
      getPendingFiles,
      getDeductionStatus,
      openBackupRequestModal,
      setOpenBackupRequestModal,
      raiseBackupRequest,
    }),
    [
      remittanceItemId,
      deductionResolutionStatus,
      documents,
      isLoadingDeductionStatus,
      importedFiles,
      purchaserKey,
      importedFilesAsDocuments,
      getAllFilesAsDocuments,
      deleteDeductionBackupDocument,
      getDeductionStatus,
      getPendingFiles,
      openBackupRequestModal,
      setOpenBackupRequestModal,
      raiseBackupRequest,
    ]
  );

  return <DeductionIncompleteContext.Provider value={contextValue}>{children}</DeductionIncompleteContext.Provider>;
};
